import React from 'react';
import {
  TableBody,
  TableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  Paper,
  Box,
  useTheme,
} from '@mui/material';
import withTableStateAndUniqueBackfill from './providers/withTableState';
import { useTableState } from './providers/TableStateProvider';
import TableHeaderCell from './components/header/TableHeaderCell';
import TableFooter from './components/footer/TableFooter';
import TableRow from './components/TableRow';
import TableWrapper from './components/TableWrapper';
import type { TableProps } from './types';
import TableFilters from './components/filters/TableFilters';
import TableSearch from './components/TableSearch';
import RefreshIcon from './components/RefreshIcon';
import ColumnSelector from './components/ColumnSelector';
import CSVFileExport from './components/CSVFileExport';
import { TableSkeleton } from './components/TableSkeleton';
import { Show } from '../../Show';

const AndromedaTableBase = ({
  children = <DefaultTableChildren />,
  showFooter = true,
  customRowComponent: CustomRowComponent,
  ...props
}: AndromedaTableProps) => {
  const tableState = useTableState();
  const theme = useTheme();

  return (
    <>
      {children}
      <Paper
        elevation={props.tableElevation ?? 6}
        style={{ overflowX: 'auto' }}
      >
        <TableWrapper height={props.height}>
          {tableState.showHeader !== false && (
            <TableHead>
              <MuiTableRow
                sx={{
                  height: '3rem',
                  backgroundColor: theme.serviceTable.headerCell.bgColor,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  borderBottom: 'none',
                }}
              >
                {tableState.table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHeaderCell key={header.id} header={header} />
                    ))}
                  </React.Fragment>
                ))}
              </MuiTableRow>
            </TableHead>
          )}
          <TableBody>
            {props.isFetching ? (
              <Show when={!!props.skeleton}>
                {props.skeleton?.component || (
                  <TableSkeleton
                    columns={tableState.table.getAllColumns()}
                    rowCount={props.skeleton?.rowCount}
                  />
                )}
              </Show>
            ) : (
              tableState.table
                .getRowModel()
                .rows.map((row) =>
                  CustomRowComponent ? (
                    <CustomRowComponent row={row} key={row.id} />
                  ) : (
                    <TableRow row={row} key={row.id} />
                  )
                )
            )}
            {!props.isFetching &&
              tableState.table.getRowModel().rows.length === 0 && (
                <MuiTableRow>
                  <MuiTableCell
                    colSpan={tableState.table.getAllColumns().length}
                    align="center"
                    sx={{ border: 'none' }}
                  >
                    {props.noRecordsMessage ?? 'No data available'}
                  </MuiTableCell>
                </MuiTableRow>
              )}
          </TableBody>
        </TableWrapper>

        {showFooter && <TableFooter />}
      </Paper>
    </>
  );
};

export default withTableStateAndUniqueBackfill(AndromedaTableBase);

type AndromedaTableProps = {
  children?: React.ReactNode;
  showFooter?: boolean;
  customRowComponent?: React.ComponentType<{ row: any }>;
} & Partial<TableProps>;

const DefaultTableChildren = () => {
  return (
    <Box display="flex" justifyContent="end" pr={1.5} mb={1}>
      <Box display="flex" gap="0.75rem">
        <TableFilters />
        <TableSearch />
        <RefreshIcon />
        <ColumnSelector />
        <CSVFileExport />
      </Box>
    </Box>
  );
};
